/**
 * Slideshow Module
 * Uses two synchronized Splide instances:
 * - Content slider with fade effect
 * - Image slider with slide effect
 */

import Splide from "@splidejs/splide";
import { log } from "./utils.js";

/**
 * Initialize all slideshows on the page
 */
export function initSlideshow() {
    const slideshows = document.querySelectorAll("[data-slideshow]");

    if (slideshows.length === 0) {
        return;
    }

    slideshows.forEach((element) => {
        const contentSlider = element.querySelector("[data-slideshow-content]");
        const imageSlider = element.querySelector("[data-slideshow-images]");

        if (!contentSlider || !imageSlider) {
            log("Slideshow: Missing content or image slider");
            return;
        }

        const speed = parseInt(element.dataset.splideSpeed, 10) || 1000;
        const rewind = element.dataset.splideRewind === "1";
        const autoplay = element.dataset.splideAutoplay === "1";
        const interval = parseInt(element.dataset.splideInterval, 10) || 5000;
        const pauseOnHover = element.dataset.splidePauseOnHover === "1";

        // Content slider - fade effect
        const contentSplide = new Splide(contentSlider, {
            type: "fade",
            rewind: rewind,
            speed: speed,
            autoplay: autoplay,
            interval: interval,
            pauseOnHover: pauseOnHover,
            arrows: false,
            pagination: false,
            drag: false,
        });

        // Image slider - slide effect with easing
        const imageSplide = new Splide(imageSlider, {
            type: "slide",
            rewind: rewind,
            speed: 1000,
            easing: "cubic-bezier(0.33, 1, 0.68, 1)", // easeOutCubic
            autoplay: false, // Controlled by content slider
            arrows: false,
            pagination: false,
        });

        // Reset zoom on all slides before move, apply zoom-out on active after move
        imageSplide.on("move", () => {
            const slides = imageSlider.querySelectorAll(".splide__slide");
            slides.forEach((slide) => {
                slide.classList.remove("is-zooming");
            });
        });

        imageSplide.on("moved", (newIndex) => {
            const activeSlide = imageSlider.querySelectorAll(".splide__slide")[newIndex];
            if (activeSlide) {
                // Small delay to ensure the slide transition has started
                requestAnimationFrame(() => {
                    activeSlide.classList.add("is-zooming");
                });
            }
        });

        // Sync the sliders
        contentSplide.sync(imageSplide);

        // Mount both sliders
        contentSplide.mount();
        imageSplide.mount();

        // Set initial zoom state on first slide
        const firstSlide = imageSlider.querySelector(".splide__slide.is-active");
        if (firstSlide) {
            firstSlide.classList.add("is-zooming");
        }

        // Custom navigation buttons
        const prevBtn = element.querySelector("[data-slideshow-prev]");
        const nextBtn = element.querySelector("[data-slideshow-next]");
        const currentEl = element.querySelector(".b-slideshow__current");

        if (prevBtn) {
            prevBtn.addEventListener("click", () => {
                contentSplide.go("<");
            });
        }

        if (nextBtn) {
            nextBtn.addEventListener("click", () => {
                contentSplide.go(">");
            });
        }

        // Update counter on slide change
        if (currentEl) {
            contentSplide.on("move", (newIndex) => {
                currentEl.textContent = newIndex + 1;
            });
        }

        // Update button states
        const updateButtonStates = () => {
            if (!rewind) {
                const index = contentSplide.index;
                const length = contentSplide.length;

                if (prevBtn) {
                    prevBtn.disabled = index === 0;
                }
                if (nextBtn) {
                    nextBtn.disabled = index === length - 1;
                }
            }
        };

        contentSplide.on("move", updateButtonStates);
        updateButtonStates();
    });

    log(`Initialized ${slideshows.length} slideshow(s)`);
}

export default initSlideshow;
